/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import type { UniversalCheckoutOptions } from '@primer-io/checkout-web'
import { ref, onMounted, onBeforeUnmount, unref, type MaybeRef } from 'vue'

import usePaymentSession from '@render/composables/usePaymentSession'
import { usePipe, ERROR_CONTEXTS } from '@render/composables/usePipe'
import { waitForPipeData } from '@render/composables/waitForPipeData'
import { loadPrimerSdk, type PrimerSdk } from '@render/integrations/primer/primerSdk'
import {
  recordLatestPaymentSession,
  recordCompletedPaymentSession,
} from '@render/composables/paymentSessionStore'
import { setDeviceTimestampOnce } from '@render/lib/deviceTimestamps'

// pipesControllerUpdate
// pipesControllerFindOneForUpdate

// Subset of Primer's `CheckoutCompleteHookData` we care about. The full SDK
// type carries paymentInstrumentData / paymentInstrumentType too, which we
// don't currently use. `payment` may be `null` (matching Primer's nullable).
export type PrimerCheckoutCompleteData = {
  payment: { id?: string; paymentInstrumentType?: string } | null
}

// Cached entry stored on `pipe` state under `var.global.primerSession`. See
// PRIMER_TOKEN.md for the full lifecycle (issuance, expiry, invalidation).
export type CachedPrimerSession = {
  clientToken: string
  clientTokenExpirationDate: string
  paymentDataKey: string
}

// `payments-api` may decide a customer can be charged immediately (e.g. via a
// vault token / saved payment method). In that case the response carries
// `instantlyCharged: true` and no `clientToken` — there is nothing to render
// in the Primer iframe. The composable surfaces this via a sentinel so the
// caller can fire `onCheckoutComplete` instead of trying to render. Legacy
// parity: https://github.com/cppv3/pipelines/blob/main/src/cf-e/src/js/primer.js#L98-L101.
type SessionResult = { kind: 'token'; entry: CachedPrimerSession } | { kind: 'instantly-charged' }

// Pipe-state key used to cache the Primer client session across re-mounts and
// sibling checkout instances within the same pipe session. Mirrors legacy
// `dataStore.values.primerSession` from
// https://github.com/cppv3/pipelines/blob/main/lib/ckd-kg-f/js/dataStore.js#L420-L430.
const PRIMER_SESSION_KEY = 'var.global.primerSession'

// Refetch margin: if the cached token expires within this window we treat it
// as already invalid and refetch on next mount.
const EXPIRY_BUFFER_MS = 60_000

// How long checkout waits for a still-in-flight order create/upsert to land
// `var.global.orderId` / `var.global.paymentsApiCustomerId` before declaring
// the missing-prereq error. First window is sized to the pipeorders POST
// deadline (10s) plus write-back margin; when it misses, a second window
// covers pipeorders' background retry chain (backoff 2s/5s, ≤10s each)
// before giving up. Total spinner worst case ≈ 30s — long, but the
// alternative is a guaranteed-dead checkout.
const PREREQ_WAIT_MS = 12_000
const PREREQ_RETRY_WAIT_MS = 18_000
const PREREQ_KEYS = ['var.global.orderId', 'var.global.paymentsApiCustomerId']

// Backoff for retrying Primer's own core-SDK CDN load inside
// showUniversalCheckout (its "Can't load Primer SDK" rejection — see
// isSdkLoadError). Mirrors pipeorders' upsert retry chain. The user sees the
// checkout spinner for the duration (isLoading stays true).
const SDK_LOAD_RETRY_BACKOFF_MS = [2_000, 5_000]

// Stable serialization of the paymentData payload — used as a cache fingerprint
// so a mid-flow change (different offer, different email, etc.) invalidates the
// previously-issued token, which was bound to the old payload server-side.
const paymentDataKey = (data: Record<string, unknown>): string => {
  const sorted = Object.keys(data)
    .sort()
    .map((k) => [k, data[k]] as const)
  return JSON.stringify(sorted)
}

const isExpired = (entry: CachedPrimerSession): boolean => {
  const expiresAt = Date.parse(entry.clientTokenExpirationDate)
  if (Number.isNaN(expiresAt)) return true
  return expiresAt - Date.now() <= EXPIRY_BUFFER_MS
}

// `paymentData` is supplied as a thunk rather than a plain object: the payload
// reads `var.global.*` from pipe state, but on hard reload (and after dev-tools
// reset) the localStorage→variables merge runs inside `setTimeout(0)` (see
// usePipe.ts:567 — comments around `loadPersistedData`). PrimerCheckout's
// `setup()` runs synchronously in the same render frame, so a captured-at-setup
// `paymentData` would always read undefined for persisted fields. The thunk
// lets us evaluate the payload AFTER `var.system.persistedDataLoaded` flips to
// true, mirroring the documented gate.
type PaymentDataPayload = { [key: string]: string | number | boolean | unknown }
export type PrimerCardPlaceholders = {
  cardNumber?: string
  expiryDate?: string
  cvv?: string
  cardholderName?: string
}
type Props = {
  paymentData: PaymentDataPayload | (() => PaymentDataPayload)
  styleOverrides?: MaybeRef<{ [key: string]: string | number | boolean | unknown }>
  // Translated label for Primer's built-in submit ("Pay") button. Primer
  // renders that button inside its own SDK and localizes the text from its
  // `locale` only, with no API to set it — so we override the button's text
  // node with this value whenever Primer (re)renders it (see the submitButton
  // callbacks in getPrimerOptions). Thunk so it re-reads the active-language
  // translation on each apply. Returns undefined to leave Primer's native text.
  submitButtonLabel?: () => string | undefined
  /** The author's card field placeholders; an unset one keeps Primer's. */
  cardPlaceholders?: () => PrimerCardPlaceholders
  // Predicate evaluated after `pipe.whenReady()` in `onMounted`. When it
  // returns true the composable skips the payments-api POST and the SDK
  // render, and fires `onCheckoutComplete?.()` instead — same path as the
  // `instantly-charged` branch. Used to short-circuit revisits to a
  // checkout for an already-purchased PPO (see usePurchasedPpos.ts).
  shouldSkip?: () => boolean
  // When set, a Bugsnag error is fired if `onPrimerCheckoutLoadedConfirmation`
  // has not fired within this many seconds after the checkout render completes.
  // Identifies users who reach the checkout page but never see the card form.
  checkoutLoadTimeoutSeconds?: () => number | undefined
  // Consulted before the composable initiates a reinit on its own (the
  // 'online' self-heal). Lets the consumer veto while a payment is in flight:
  // a self-initiated reinit that reaches renderPrimerCheckout would
  // clearContainer() + re-render, ripping the live form out mid-charge. (A
  // reinit that ERRORS before rendering now also clears the container — via
  // reinitCheckout's finally, so the stale form doesn't linger under the error
  // message — but that path can't run mid-charge anyway, since reinit is only
  // self-initiated when idle.) Consumer-initiated reinitCheckout calls are not
  // gated.
  canReinit?: () => boolean
  callbackFns: {
    onPaymentCreationStart?: () => void
    // Forwards Primer's `data.payment.id` so consumers can pin the NPS
    // payment id needed for the dev-only webhook replay (see
    // /workspace/ui/src/render/integrations/pipeorders/pipeorders.ts).
    onCheckoutComplete?: (data?: PrimerCheckoutCompleteData) => void
    onCheckoutFail?: () => void
    onPaymentMethodAction?: () => void
    onBeforePaymentCreate?: () => void
    // Fires the moment the user taps the Google Pay / PayPal button (Primer's
    // googlePay.onClick / paypal.onClick), before the wallet sheet/popup opens.
    // The leading edge for exit-intent suppression: it runs in the page's JS
    // context even for the PayPal button, which renders inside a cross-origin
    // iframe a DOM pointerdown could never reach.
    onWalletPaymentStart?: () => void
    // Fires when the user dismisses a wallet/APM popup (e.g. PayPal "window
    // closed") without completing - a soft cancellation. Lets the consumer
    // release payment-in-flight state (e.g. exit-intent suppression).
    onUserCancellation?: () => void
    // Fires once Primer.showUniversalCheckout has rendered the iframe (or after
    // a re-init). Mirrors legacy
    // https://github.com/cppv3/pipelines/blob/main/lib/common/js/components/primer.js#L111
    // where INITIATE_CHECKOUT was tracked at this exact moment.
    onCheckoutRendered?: () => void
    onPrimerCheckoutRequested?: () => void
    onPrimerCheckoutLoaded?: () => void
    onPrimerCheckoutOnBeforePaymentCreated?: (customData: Record<string, unknown>) => void
    onPrimerCheckoutLoadedConfirmation?: () => void
    // Fires alongside the existing Bugsnag abandonment report below (checkout
    // was requested but the user left before the card form/confirmation loaded).
    onCheckoutAbandoned?: () => void
  }
  isBrowser?: boolean
}

export default function usePrimer(props: Props) {
  const isLoading = ref(true)
  const isError = ref(false)
  const pipe = usePipe()

  // Tracks whether the confirmation view (card form) successfully loaded.
  // Set to true by onPrimerCheckoutLoadedConfirmation; used to suppress both
  // the load-timeout alert and the abandonment alert when the user reached checkout.
  let confirmationLoaded = false
  // Tracks whether showUniversalCheckout was called at least once. Abandonment
  // is only meaningful if the user left after the checkout started rendering
  // but before the card form (confirmation) appeared.
  let checkoutRequested = false
  let requestedAt: number | null = null
  // Timer handle for the confirmation-load timeout alert.
  let loadTimeoutTimer: ReturnType<typeof setTimeout> | null = null
  // Active MutationObserver watching for the card-number field. Kept at this
  // scope so onBeforeUnmount can disconnect it — without this, navigating away
  // and back creates a second observer on the same document.body, causing
  // double confirmation events on the next render.
  let cardFieldObserver: MutationObserver | null = null
  // pageshow handler stored so it can be removed on unmount.
  let pagesShowHandler: ((e: PageTransitionEvent) => void) | null = null
  // 'online' handler stored so it can be removed on unmount.
  let onlineHandler: (() => void) | null = null
  // Latches an 'online' edge that arrived while an attempt was in flight.
  // 'online' only fires on interface-up transitions — a captive portal login
  // that happens after the edge produces no second event — so a dropped edge
  // could park an errored checkout forever. Consumed (at most once per edge)
  // when the in-flight attempt settles in error.
  let pendingOnlineReinit = false
  // Guards all post-await callbacks — showUniversalCheckout can resolve after
  // the component unmounts in a SPA, so we must not fire events into a dead instance.
  let unmounted = false

  // Tracks Primer's built-in submit button loading state (spinner shown). While
  // loading we must NOT overwrite the button's text — that would rip out
  // Primer's spinner. Set by the submitButton.onLoading callback; the label is
  // (re)applied once loading clears.
  let submitButtonLoading = false

  // Overwrites Primer's built-in submit button text with the translated label.
  // Primer exposes no label API, so we replace the text node directly and
  // re-apply on every Primer-signalled button change (onVisible /
  // onContentChange / onLoading→false). No-op when the consumer supplies no
  // label (leaves Primer's native localized text) or while the button is in its
  // loading state.
  const applySubmitButtonLabel = () => {
    if (!props.isBrowser || submitButtonLoading) return
    const label = props.submitButtonLabel?.()
    if (!label) return
    const btn = document.querySelector('#primer-checkout-submit-button')
    // Guard the write so we never trigger a needless DOM mutation (and any
    // onContentChange re-entrancy) when the text already matches.
    if (btn && btn.textContent !== label) btn.textContent = label
  }

  // Prevent leaked observer that may keep firing on
  // the next page's mutations forever.
  let checkoutObserver: MutationObserver | null = null
  let pageShowHandler: ((e: PageTransitionEvent) => void) | null = null

  const clearLoadTimeoutTimer = () => {
    if (loadTimeoutTimer !== null) {
      clearTimeout(loadTimeoutTimer)
      loadTimeoutTimer = null
    }
  }

  const cleanupMutationObserver = () => {
    checkoutObserver?.disconnect()
    checkoutObserver = null
    if (pageShowHandler) {
      window.removeEventListener('pageshow', pageShowHandler)
      pageShowHandler = null
    }
  }

  const getOrderContext = () => ({
    orderId: resolvedPaymentData['orderId'],
    customerId: resolvedPaymentData['customerId'],
    priceProductObjectId: resolvedPaymentData['priceProductObjectId'],
    timestamp: new Date().toISOString(),
    waitedSeconds: requestedAt !== null ? Math.round((Date.now() - requestedAt) / 1000) : undefined,
  })

  const resolvePaymentData = (): PaymentDataPayload =>
    typeof props.paymentData === 'function' ? props.paymentData() : props.paymentData

  // Captured fresh inside `onMounted` AFTER persisted data has loaded; until
  // then it would read undefined for every persisted field. Storing it on a
  // ref so `getOrFetchPrimerSession` / cache-key checks can see the same value
  // we ultimately POSTed.
  let resolvedPaymentData: PaymentDataPayload = {}
  let currentDataKey = ''

  // The raw payments-api response from the most recent fetch made by THIS
  // composable instance — a local closure variable, not shared pipe state,
  // so it can't be clobbered by an unrelated fetch from another component
  // (Yuno, SavedCard, a sibling PrimerCheckout). Used only by
  // `recordCompletion` to record the session behind an actual completion —
  // reading the shared `latest` slot at completion time would be racy.
  let lastFetchedSession: Record<string, unknown> | null = null

  const REQUIRED_FIELDS = ['customerId', 'orderId', 'priceProductObjectId'] as const
  const checkPrereqs = (
    payload: PaymentDataPayload,
  ): { ok: boolean; missing: readonly string[] } => {
    const missing = REQUIRED_FIELDS.filter((f) => {
      const v = payload[f]
      return v === undefined || v === null || v === ''
    })
    return { ok: missing.length === 0, missing }
  }

  const getPrimerOptions = (PaymentFlow: PrimerSdk['PaymentFlow']): UniversalCheckoutOptions => {
    let style = {
      formSpacings: {
        betweenLabelAndInput: '12px',
        betweenInputs: '16px',
      },
      inputLabel: {
        fontFamily: 'Avenir, Arial, sans-serif',
        fontSize: '12px',
        lineHeight: 'normal',
        fontWeight: 'normal',
        color: '#212121',
      },
      input: {
        base: {
          background: '#fff',
          color: '#35383C',
        },
      },
      inputErrorText: {
        fontSize: '14px',
        fontWeight: '400',
      },
      submitButton: {
        base: {
          fontFamily: 'JosefinSans, sans-serif',
          color: '#fff',
          background: '#46A477',
          fontSize: '20px',
          fontWeight: 'bold',
          borderRadius: '6px',
        },
      },
    }
    if (props.styleOverrides) {
      style = { ...style, ...unref(props.styleOverrides) }
    }

    const locale = pipe.getData('const.system.language')
      ? (pipe.getData('const.system.language') as string)
      : 'en'

    const placeholders = props.cardPlaceholders?.() ?? {}

    return {
      container: '#checkout-container',
      locale: locale,
      vault: {
        visible: false,
      },
      successScreen: false,
      errorMessage: { disabled: true },
      onPaymentCreationStart: () => {
        if (props.callbackFns.onPaymentCreationStart) {
          props.callbackFns.onPaymentCreationStart()
        }
      },
      onCheckoutComplete: async (data: PrimerCheckoutCompleteData) => {
        clearLoadTimeoutTimer()
        recordCompletion()
        if (props.callbackFns.onCheckoutComplete) {
          props.callbackFns.onCheckoutComplete(data)
        }
      },
      onCheckoutFail: async (error, { payment }) => {
        if (props.callbackFns.onCheckoutFail) {
          props.callbackFns.onCheckoutFail()
        }
      },
      onPaymentMethodAction: (action, data) => {
        if (props.callbackFns.onPaymentMethodAction) {
          props.callbackFns.onPaymentMethodAction()
        }
      },
      onBeforePaymentCreate: ({ paymentMethodType }, handler) => {
        if (props.callbackFns.onBeforePaymentCreate) {
          props.callbackFns.onBeforePaymentCreate()
        }
        if (props.callbackFns.onPrimerCheckoutOnBeforePaymentCreated) {
          const paymentMethodTrackingNames: { [key: string]: string } = {
            payment_card: 'Credit Card Submitted',
            google_pay: 'Google Pay Selected',
            apple_pay: 'Apple Pay Selected',
            paypal: 'PayPal Selected',
          }

          const clickName = paymentMethodType
            ? paymentMethodTrackingNames[paymentMethodType.toLowerCase()]
            : paymentMethodType
          const customData: Record<string, unknown> = {}
          customData.clickName = clickName

          props.callbackFns.onPrimerCheckoutOnBeforePaymentCreated(customData)
        }

        handler.continuePaymentCreation()
      },
      submitButton: {
        useBuiltInButton: true,
        // Re-apply the translated label on every Primer-signalled button
        // change: onVisible (first render / scene change), onContentChange
        // (amount/label recompute), and onLoading→false (spinner cleared).
        onVisible: () => applySubmitButtonLabel(),
        onContentChange: () => applySubmitButtonLabel(),
        onDisable: () => {},
        onLoading: (isLoading: boolean) => {
          submitButtonLoading = isLoading
          if (!isLoading) applySubmitButtonLabel()
        },
      },
      card: {
        cardholderName: {
          placeholder: placeholders.cardholderName ?? '',
        },
        ...(placeholders.cardNumber
          ? { cardNumber: { placeholder: placeholders.cardNumber } }
          : {}),
        ...(placeholders.expiryDate
          ? { expiryDate: { placeholder: placeholders.expiryDate } }
          : {}),
        ...(placeholders.cvv ? { cvv: { placeholder: placeholders.cvv } } : {}),
      },
      paypal: {
        buttonColor: 'gold',
        buttonShape: 'rect',
        // TODO: known PayPal mismatch.
        //
        // PayPal can run a payment two ways:
        //   - "capture":  just charge the customer once, right now.
        //   - "tokenize": charge them AND save their PayPal account so we can
        //                 charge it again later (a subscription / rebill - the
        //                 saved permission is called a "billing agreement").
        //
        // The `PREFER_VAULT` below asks PayPal to save the account (the
        // "tokenize" way). But the payment our payments-api sets up is
        // "capture" - charge once, save nothing - because the frontend sends
        // `restrictSaveCard: true` (see PrimerCheckout.vue). PayPal sees the two
        // disagree and logs the "expected intent=tokenize, got capture"
        // smart_button warnings to the console.
        //
        // Options:
        //   - Rebill via PayPal: set the session up as "tokenize" in
        //     payments-api, and keep PREFER_VAULT here.
        //   - One single charge: switch this to PaymentFlow.DEFAULT and the
        //     warnings stop.
        paymentFlow: PaymentFlow.PREFER_VAULT,
        // Leading edge for exit-intent suppression - fires before the PayPal
        // popup opens, crossing the paypal.com iframe boundary for us.
        onClick: () => props.callbackFns.onWalletPaymentStart?.(),
      },
      googlePay: {
        captureBillingAddress: true,
        // Leading edge for exit-intent suppression - fires before the Google
        // Pay sheet opens.
        onClick: () => props.callbackFns.onWalletPaymentStart?.(),
      },
      applePay: {
        captureBillingAddress: true,
        // No onClick here on purpose: Primer's ApplePayOptions doesn't expose
        // one, and Apple Pay's sheet is a native overlay that doesn't background
        // the tab - so it never fires the false visibilitychange→hidden that the
        // GPay/PayPal popups do, and needs no leading-edge suppression. It's
        // still covered from onBeforePaymentCreate (phase='charging') onward.
      },
      style,
    }
  }

  // Remove primer's checkout before reinit
  const clearContainer = () => {
    if (!props.isBrowser) return

    const container = document.querySelector('#checkout-container')
    if (container) container.innerHTML = ''
  }

  const clearPrimerSession = () => {
    pipe.setData(PRIMER_SESSION_KEY, null)
  }

  // Writes the session behind THIS specific completion into the
  // provider-agnostic paymentSession.completed slot, using the raw response
  // captured at fetch time (see lastFetchedSession) rather than the shared
  // `latest` slot. No-ops if nothing was ever fetched this mount (e.g. the
  // `shouldSkip` short-circuit, which completes without fetching anything).
  const recordCompletion = () => {
    if (lastFetchedSession) recordCompletedPaymentSession(pipe, lastFetchedSession)
  }

  // Detects Primer's "expired client token" runtime error. The SDK doesn't
  // expose a typed code so we string-match on the message — same approach as
  // legacy primer.js#L57-L58.
  const isExpiredTokenError = (err: unknown): boolean => {
    const msg = (err instanceof Error ? err.message : String(err ?? '')).toLowerCase()
    return msg.includes('clienttoken') && msg.includes('expired')
  }

  // Primer's npm package is a loader shim: showUniversalCheckout fetches
  // PrimerCoreSDK.min.js + Checkout.css from sdk.primer.io at render time and
  // rejects with "Can't load Primer SDK: <url>" (or "... SDK's Stylesheet:
  // <url>") when a fetch fails. The shim resets its internal load promise on
  // rejection, so re-calling showUniversalCheckout re-fetches — a transient
  // failure (connectivity just restored, flaky mobile network) is recoverable
  // by plain retry. No typed error code exists for this either.
  const isSdkLoadError = (err: unknown): boolean => {
    const msg = (err instanceof Error ? err.message : String(err ?? '')).toLowerCase()
    return msg.includes("can't load primer sdk")
  }

  // PayPal SDK throws "Window closed before response" when the user closes the
  // PayPal popup without completing payment. Primer propagates this as an
  // exception from showUniversalCheckout. Treat it as a soft cancellation —
  // don't set isError, don't re-throw; the checkout UI remains intact so the
  // user can choose a different payment method or try again.
  const isUserCancellation = (err: unknown): boolean => {
    const msg = (err instanceof Error ? err.message : String(err ?? '')).toLowerCase()
    return msg.includes('window closed') || msg.includes('closed before response')
  }

  // Apple Pay sessions close asynchronously. When onCheckoutFail fires and
  // reinitCheckout is called immediately, the browser's ApplePaySession may
  // still be active, causing showUniversalCheckout to throw InvalidAccessError.
  // Detected by error name or message — retry once after a short yield.
  const isActivePaymentSession = (err: unknown): boolean => {
    const name = err instanceof Error ? err.name : ''
    const msg = (err instanceof Error ? err.message : String(err ?? '')).toLowerCase()
    return name === 'InvalidAccessError' || msg.includes('active payment session')
  }

  const fetchFreshSession = async (): Promise<SessionResult> => {
    const {
      isError: fetchError,
      data,
      fetchPaymentSession,
    } = usePaymentSession(
      pipe.getData('const.global.paymentsApiUrl') as string,
      resolvedPaymentData,
    )
    setDeviceTimestampOnce(pipe, 'paymentSessionCreated')
    await fetchPaymentSession()
    if (fetchError.value) throw new Error('Error fetching payment session')

    const session = data.value
    if (!session) throw new Error('Payment session response was empty')
    lastFetchedSession = { ...session }

    // payments-api charges immediately when a saved payment method is
    // available; no Primer SDK render needed. Bypass cache (we want each
    // mount to re-check the server) and signal the caller to fire
    // onCheckoutComplete. Legacy parity:
    // https://github.com/cppv3/pipelines/blob/main/src/cf-e/src/js/primer.js#L98-L101
    if (session.instantlyCharged) {
      return { kind: 'instantly-charged' }
    }

    if (!session.clientToken || !session.clientTokenExpirationDate) {
      throw new Error('Payment session response missing clientToken or expiry')
    }

    const entry: CachedPrimerSession = {
      clientToken: session.clientToken,
      clientTokenExpirationDate: session.clientTokenExpirationDate,
      paymentDataKey: currentDataKey,
    }
    pipe.setData(PRIMER_SESSION_KEY, entry)
    // Provider-agnostic mirror for consumers that don't care which checkout
    // provider produced the session (e.g. GTM, saved-card correlation) — see
    // paymentSessionStore.ts. Purely additive: doesn't feed usePrimer's own
    // cache/expiry logic above, which still reads only PRIMER_SESSION_KEY.
    // Spread the raw response first so every payments-api field (amount,
    // currency, trackingEvent, ...) survives, then layer `entry` on top.
    recordLatestPaymentSession(pipe, { ...session, ...entry })

    return { kind: 'token', entry }
  }

  const getOrFetchPrimerSession = async (): Promise<SessionResult> => {
    const cached = pipe.getData(PRIMER_SESSION_KEY) as CachedPrimerSession | null | undefined

    if (cached?.clientToken && cached.paymentDataKey === currentDataKey && !isExpired(cached)) {
      return { kind: 'token', entry: cached }
    }

    return fetchFreshSession()
  }

  const renderPrimerCheckout = async (entry: CachedPrimerSession): Promise<void> => {
    const sdk = await loadPrimerSdk()
    // Component may have unmounted while we were loading the SDK (e.g. user
    // hit back before the network request finished). Bail before touching the
    // DOM so Primer never tries to mount into a detached #checkout-container.
    if (unmounted) return
    clearContainer()
    // Arm the confirmation-detecting MutationObserver only now, right after the
    // stale DOM (a previous render's card form, or nothing on first mount) has
    // been cleared. Arming it any earlier lets it match a still-present
    // PREVIOUS card-number field on the next unrelated childList mutation
    // anywhere in the document — observerCallback's querySelector can't tell
    // "already there" from "just inserted" — firing
    // onPrimerCheckoutLoadedConfirmation instantly on reinit, before
    // onPrimerCheckoutRequested/Loaded even run. Reset confirmationLoaded here
    // too so timeout/abandonment tracking reflects this attempt, not a stale one.
    confirmationLoaded = false
    addMutationObserver()
    const options = getPrimerOptions(sdk.PaymentFlow)

    // Analytics trade-off: onPrimerCheckoutRequested fires once per ATTEMPT,
    // so every retry path that re-enters this function (SDK-load backoff,
    // expired-token refetch, Apple Pay yield) emits an extra "Primer Checkout
    // Requested" / "checkoutRequested" pair while "loaded"/"rendered" fire at
    // most once — a mount that needs both SDK retries emits requested 3x.
    // Deliberate: per-attempt emission is what the load-failure funnels
    // measure; requested-vs-rendered ratios must treat requested as
    // attempts, not sessions.
    const trySingleRender = async (clientToken: string) => {
      if (unmounted) return
      checkoutRequested = true
      requestedAt = Date.now()
      props.callbackFns.onPrimerCheckoutRequested?.()
      await sdk.Primer.showUniversalCheckout(clientToken, options)
      if (unmounted) return
      props.callbackFns.onPrimerCheckoutLoaded?.()
    }

    try {
      await trySingleRender(entry.clientToken)
    } catch (err) {
      if (isUserCancellation(err)) {
        props.callbackFns.onUserCancellation?.()
        return
      }
      // Apple Pay sessions close asynchronously. If onCheckoutFail triggers a
      // reinit before the previous ApplePaySession has fully torn down, the
      // browser throws InvalidAccessError. Yield 300 ms and retry once — that
      // is enough for the session to close without being noticeable to the user.
      if (isActivePaymentSession(err)) {
        console.warn('[primer] Active payment session detected — retrying after yield', err)
        await new Promise((resolve) => setTimeout(resolve, 300))
        clearContainer()
        await trySingleRender(entry.clientToken)
        return
      }
      // Transient CDN failure loading Primer's core SDK / stylesheet —
      // typically the user's connectivity just came back mid-funnel and the
      // fresh connection to sdk.primer.io still failed. Retrying
      // showUniversalCheckout re-fetches (see isSdkLoadError), so back off and
      // retry a bounded number of times before surfacing the error.
      if (isSdkLoadError(err)) {
        let lastErr: unknown = err
        let retryToken = entry.clientToken
        for (const delayMs of SDK_LOAD_RETRY_BACKOFF_MS) {
          console.warn(
            `[primer] Primer core SDK failed to load — retrying in ${delayMs}ms`,
            lastErr,
          )
          await new Promise((resolve) => setTimeout(resolve, delayMs))
          if (unmounted) return
          try {
            clearContainer()
            await trySingleRender(retryToken)
            // Mirrors the post-catch emission below — this return skips it.
            if (!unmounted) props.callbackFns.onCheckoutRendered?.()
            return
          } catch (retryErr) {
            if (isUserCancellation(retryErr)) {
              props.callbackFns.onUserCancellation?.()
              return
            }
            // The token can die while we are busy retrying the SDK load
            // (server-side invalidation). The expired-token branch below is
            // unreachable from inside this loop, so refetch inline and let
            // the next iteration render with the fresh token.
            if (isExpiredTokenError(retryErr)) {
              console.warn(
                '[primer] client token expired during SDK-load retries — refetching',
                retryErr,
              )
              clearPrimerSession()
              const fresh = await fetchFreshSession()
              if (fresh.kind !== 'token') {
                recordCompletion()
                props.callbackFns.onCheckoutComplete?.()
                return
              }
              retryToken = fresh.entry.clientToken
            }
            lastErr = retryErr
          }
        }
        if (unmounted) return
        console.error('[primer] Primer core SDK still failing after retries', lastErr)
        throw lastErr
      }
      // Legacy parity: refetch-once on expired token. See
      // https://github.com/cppv3/pipelines/blob/main/src/cf-e/src/js/primer.js#L56-L70
      if (!isExpiredTokenError(err)) {
        // If the component unmounted while showUniversalCheckout was in flight,
        // the "#checkout-container" element is gone — expected, not an error.
        if (unmounted) return
        console.error('[primer] showUniversalCheckout failed (non-expiry error)', err)
        throw err
      }
      console.warn('[primer] Primer client token rejected as expired — refetching once.', err)
      clearPrimerSession()
      const fresh = await fetchFreshSession()
      // The retry path was triggered by an expired-token error from a token
      // we already rendered, so server flipping to instantly-charged on
      // refetch is vanishingly unlikely. Hand it back to the caller anyway —
      // they'll surface it as a checkout-complete on the next mount.
      if (fresh.kind !== 'token') {
        console.warn(
          '[primer] retry-after-expired returned instantlyCharged — firing onCheckoutComplete and bailing out of render',
        )
        recordCompletion()
        props.callbackFns.onCheckoutComplete?.()
        return
      }
      clearContainer()
      await trySingleRender(fresh.entry.clientToken)
    }

    // Legacy parity:
    // https://github.com/cppv3/pipelines/blob/main/lib/common/js/components/primer.js#L111
    // tracked INITIATE_CHECKOUT right after `Primer.showUniversalCheckout`
    // resolved. Forward this signal to the consumer so it can emit
    // `payment:checkout-rendered`.
    if (!unmounted) props.callbackFns.onCheckoutRendered?.()
  }

  const reinitCheckout = async () => {
    // onCheckoutFail can fire from a late-resolving showUniversalCheckout after
    // the component has already unmounted (see the `unmounted` flag comment
    // above) — bail before touching the DOM/observers/listeners below.
    if (unmounted) return
    // Re-snapshot payment data so mid-session changes (e.g. exit intent accepted,
    // offer swap) pick up the new PPO/pricing before we check the cache fingerprint.
    resolvedPaymentData = resolvePaymentData()
    currentDataKey = paymentDataKey(resolvedPaymentData)
    isLoading.value = true
    // A previous failed attempt leaves isError set; without clearing it a
    // successful reinit would render the checkout with the error message
    // still showing above it (the template shows both).
    //
    // Analytics trade-off: clearing isError here means a user who navigates
    // away while this reinit is in flight passes the `!isError` condition in
    // onBeforeUnmount's abandonment guard — the session is reported as
    // "abandoned" (Bugsnag + checkoutAbandoned event) where it was previously
    // classified as errored. Deliberate: a recovery attempt in flight IS a
    // loading checkout the user walked away from.
    isError.value = false
    try {
      // Mirror the mount path's refusal to POST a doomed payload (payments-api
      // rejects missing ids with 400). Reachable with missing prereqs via the
      // 'online' self-heal — e.g. a deep link that never went through the
      // funnel, or the editor preview payload. No trackError: this is the
      // same known-state skip the mount gate console-logs, and it would spam
      // Bugsnag on every connectivity flap otherwise. Ids that arrive later
      // (a slow pipeorders retry) pass this check on the next attempt.
      const prereq = checkPrereqs(resolvedPaymentData)
      if (!prereq.ok) {
        console.error(
          `[primer] reinit skipped — missing required fields: ${prereq.missing.join(', ')}`,
        )
        isError.value = true
        return
      }
      const result = await getOrFetchPrimerSession()
      if (unmounted) return
      if (result.kind === 'instantly-charged') {
        // Style watcher triggers reinit; if the server now reports instantly
        // charged, fire the completion callback rather than try to render.
        recordCompletion()
        props.callbackFns.onCheckoutComplete?.()
        return
      }
      await renderPrimerCheckout(result.entry)
    } catch (err) {
      pipe.trackError({
        error: err,
        context: ERROR_CONTEXTS.primerCheckout,
        metadata: { phase: 'reinit' },
      })
      // Render failed — show the error state so the user isn't left with an
      // empty container and no way to recover.
      isError.value = true
    } finally {
      isLoading.value = false
      // A reinit that errors BEFORE renderPrimerCheckout runs — e.g. exit
      // intent accepted while offline: the watcher clears the session then
      // reinits, and getOrFetchPrimerSession's fetch throws — never reaches the
      // clearContainer() inside the render path. The PREVIOUS successful
      // render's Primer form therefore stays mounted in #checkout-container and
      // shows UNDERNEATH the load-error message. Tear it down here so the error
      // state replaces the stale checkout instead of stacking on top of it.
      // Guarded on isError so the success path (already cleared + re-rendered
      // inside renderPrimerCheckout) is untouched — no extra clear, no flash.
      if (isError.value) clearContainer()
      replayPendingOnlineReinit()
    }
  }

  // Consumes a latched 'online' edge (see pendingOnlineReinit) after an
  // attempt settles. One replay per edge: the flag is cleared before the
  // reinit, and only a new edge can set it again — no retry loop.
  const replayPendingOnlineReinit = () => {
    if (!pendingOnlineReinit) return
    pendingOnlineReinit = false
    if (unmounted || !isError.value) return
    if (!(props.canReinit?.() ?? true)) return
    void reinitCheckout()
  }

  const addMutationObserver = () => {
    if (!props.isBrowser) return

    const observeElementInsertion = (selector: string) => {
      if (!window.MutationObserver) return

      const observerCallback = (mutationsList: any, observer: { disconnect: () => void }) => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const element = document.querySelector(selector)
            if (element) {
              confirmationLoaded = true
              clearLoadTimeoutTimer()
              props.callbackFns.onPrimerCheckoutLoadedConfirmation?.()
              observer.disconnect()
              break
            }
          }
        }
      }

      cardFieldObserver?.disconnect()
      const observer = new MutationObserver(observerCallback)
      cardFieldObserver = observer
      observer.observe(document.body, { childList: true, subtree: true })
    }

    const primerCheckoutCardNumberId = '#primer-checkout-card-number-field'

    // Start observing immediately — Primer renders later in onMounted so this
    // runs before the card-number field is inserted. Called again on every
    // reinitCheckout to re-arm the observer (it disconnects itself once it fires).
    observeElementInsertion(primerCheckoutCardNumberId)

    // Register the bfcache-restore listener only once. reinitCheckout now calls
    // addMutationObserver() on every retry — re-registering here on each call
    // would leak one 'pageshow' listener per retry (onBeforeUnmount only ever
    // removes the most recently assigned one).
    if (!pagesShowHandler) {
      pagesShowHandler = (e: PageTransitionEvent) => {
        if (e.persisted) observeElementInsertion(primerCheckoutCardNumberId)
      }
      window.addEventListener('pageshow', pagesShowHandler)
    }
  }

  onMounted(async () => {
    // Self-heal when connectivity returns: any attempt (mount or reinit) that
    // failed offline leaves isError set, and the next 'online' edge re-runs
    // the full load — session fetch, SDK load, render. Complements the
    // per-visit attempt (remount) so a user parked on the errored checkout
    // recovers without navigating. The isLoading guard skips edges that fire
    // while an attempt is already in flight.
    if (props.isBrowser) {
      onlineHandler = () => {
        if (isLoading.value) {
          // An attempt is in flight — latch the edge; the attempt's finally
          // replays it if the attempt still ends in error.
          pendingOnlineReinit = true
          return
        }
        if (!isError.value) return
        if (!(props.canReinit?.() ?? true)) return
        void reinitCheckout()
      }
      window.addEventListener('online', onlineHandler)
    }
    try {
      // Critical: defer reading payment fields until persisted-data load has
      // run. On hard reload (and after dev-tools "Clear state & return to
      // landing"), `var.global.{email,orderId,paymentsApiCustomerId,...}`
      // start out undefined and are merged from localStorage inside
      // `setTimeout(0)` (see usePipe.ts:567 commentary). Without this gate
      // PrimerCheckout would always read empty fields on its first frame and
      // POST a doomed empty payload.
      await pipe.whenReady()

      // Start the confirmation-load watchdog from mount. Measures the full
      // wait from component appearing on screen to the card form being visible.
      const timeoutSecs = props.checkoutLoadTimeoutSeconds?.()
      if (timeoutSecs !== undefined) {
        loadTimeoutTimer = setTimeout(() => {
          if (!confirmationLoaded) {
            pipe.trackError({
              error: new Error(`Primer checkout confirmation did not load within ${timeoutSecs}s`),
              context: ERROR_CONTEXTS.primerCheckoutLoadTimeout,
              metadata: { timeoutSeconds: timeoutSecs, ...getOrderContext() },
            })
          }
        }, timeoutSecs * 1000)
      }

      if (props.shouldSkip?.()) {
        // Caller's `shouldSkip` is responsible for logging the ppoId it
        // matched — usePrimer doesn't know which ppo would have been charged.
        props.callbackFns.onCheckoutComplete?.()
        return
      }
      resolvedPaymentData = resolvePaymentData()
      currentDataKey = paymentDataKey(resolvedPaymentData)

      let prereq = checkPrereqs(resolvedPaymentData)
      const hasEmailForOrder = (() => {
        const email = pipe.getData('var.global.email')
        return typeof email === 'string' && email.trim().length > 0
      })()
      if (
        !prereq.ok &&
        hasEmailForOrder &&
        prereq.missing.every((f) => f === 'customerId' || f === 'orderId')
      ) {
        // Only the order-identity fields are missing while an offer IS
        // selected (priceProductObjectId present) — the visitor came through
        // the funnel, so the order create/upsert is likely still in flight
        // (the pipeorders POST runs under a 15s deadline with a background
        // retry). Wait a bounded window for ANY producer — click-path
        // create, timeout retry, the checkout-visited upsert, AC restore —
        // to land the ids before declaring the error. `isLoading` stays true
        // for the duration, so the user sees the checkout spinner. Gated on
        // email: without one, every producer no-ops (pipeorders drops
        // emailless upserts), so waiting would be a pointless 30s spinner
        // before the same error.
        // GTM-only funnel diagnostics (dataLayer push; no-op when the gtm
        // integration is off) — lets GA4 attribute "checkout viewed but
        // errored" sessions to a missing/late order instead of a mystery.
        const emitPrereqDiagnostic = (eventName: string) => {
          const gtmEvent = pipe.getEventProcessor('gtm_event')
          if (!gtmEvent) return
          void Promise.resolve(
            gtmEvent.handler({
              args: [],
              data: { eventName, params: { missing: prereq.missing.join(',') } },
              pipe,
            }),
          ).catch((e) => pipe.trackError({ error: e, context: ERROR_CONTEXTS.eventProcessor }))
        }

        let arrived = await waitForPipeData(pipe, PREREQ_KEYS, PREREQ_WAIT_MS)
        if (unmounted) return
        if (!arrived) {
          pipe.trackError({
            error: new Error(
              `Primer checkout prereq wait timed out after ${PREREQ_WAIT_MS}ms ` +
                `(missing: ${prereq.missing.join(', ')})`,
            ),
            context: ERROR_CONTEXTS.primerCheckoutPrereqWait,
            metadata: { ...getOrderContext(), phase: 'first-window' },
          })
          emitPrereqDiagnostic('checkout_prereq_wait_timeout')

          // Second window: pipeorders' background retry chain (backoff +
          // re-POST) may still land the ids — erroring now would kill a
          // checkout that recovers seconds later. Sessions that arrive here
          // are already lost to a fast error page anyway; the extra bounded
          // wait can only win some of them back.
          arrived = await waitForPipeData(pipe, PREREQ_KEYS, PREREQ_RETRY_WAIT_MS)
          if (unmounted) return
          if (!arrived) {
            pipe.trackError({
              error: new Error(
                `Primer checkout prereq wait exhausted after ${PREREQ_WAIT_MS + PREREQ_RETRY_WAIT_MS}ms ` +
                  `(missing: ${prereq.missing.join(', ')})`,
              ),
              context: ERROR_CONTEXTS.primerCheckoutPrereqWait,
              metadata: { ...getOrderContext(), phase: 'exhausted' },
            })
            emitPrereqDiagnostic('checkout_prereq_wait_exhausted')
          }
        }
        resolvedPaymentData = resolvePaymentData()
        currentDataKey = paymentDataKey(resolvedPaymentData)
        prereq = checkPrereqs(resolvedPaymentData)
      }
      if (!prereq.ok) {
        console.error(
          `[primer] missing required fields after persisted-data load: ${prereq.missing.join(', ')}. ` +
            `Likely the user reached PrimerCheckout without going through the full funnel ` +
            `(SeedProfile → create-order → offer selection). Skipping fetch — payments-api ` +
            `would have rejected this with 400.`,
          { resolvedPaymentData },
        )

        cleanupMutationObserver()
        isError.value = true
        return
      }

      const result = await getOrFetchPrimerSession()
      if (unmounted) return
      if (result.kind === 'instantly-charged') {
        // payments-api charged the customer immediately (saved card / vault)
        // — no SDK render needed. Fire the completion callback so the
        // schema-wired `onCheckoutComplete → navigate(success)` processor
        // takes over. Legacy parity:
        // https://github.com/cppv3/pipelines/blob/main/src/cf-e/src/js/primer.js#L98-L101
        recordCompletion()
        props.callbackFns.onCheckoutComplete?.()
        return
      }
      await renderPrimerCheckout(result.entry)
    } catch (err) {
      pipe.trackError({
        error: err,
        context: ERROR_CONTEXTS.primerCheckout,
        metadata: { phase: 'render' },
      })
      cleanupMutationObserver()
      isError.value = true
    } finally {
      isLoading.value = false
      replayPendingOnlineReinit()
    }
  })

  onBeforeUnmount(() => {
    unmounted = true
    clearLoadTimeoutTimer()
    cardFieldObserver?.disconnect()
    cardFieldObserver = null

    if (pagesShowHandler) {
      window.removeEventListener('pageshow', pagesShowHandler)
      pagesShowHandler = null
    }

    if (onlineHandler) {
      window.removeEventListener('online', onlineHandler)
      onlineHandler = null
    }

    // Fire when the user leaves after checkout started rendering but before the
    // card form (confirmation) appeared — they abandoned mid-load, not after
    // a completed or errored session.
    if (props.isBrowser && checkoutRequested && !confirmationLoaded && !isError.value) {
      pipe.trackError({
        error: new Error('Primer checkout abandoned before - Primer Checkout Confirmation Loaded'),
        context: ERROR_CONTEXTS.primerCheckoutAbandoned,
        metadata: { ...getOrderContext() },
      })
      props.callbackFns.onCheckoutAbandoned?.()
    }
  })

  return { isLoading, isError, reinitCheckout, clearPrimerSession }
}
